<template>
  <div class="flex grow flex-col">
    <div class="inset-x-0 top-0 z-50 bg-white shadow lg:sticky">
      <div class="px-8 py-12 md:px-10">
        <Container>
          <div
            class="-mt-4 mb-2 text-right"
            :class="{ 'lg:hidden': display === 'form' }"
          >
            <LanguageDropdown />
          </div>

          <div
            class="lg:flex"
            :class="display === 'form' ? 'justify-between' : 'justify-center'"
          >
            <div
              v-if="display === 'form'"
              class="md:max-w-md"
            >
              <img
                :src="data.data.data.partner.logo.thumb_url"
                class="max-h-16 max-w-xxs"
              />
              <h2 class="mt-6 text-lg font-bold">
                {{ $t("personalize_your_content_experience") }}
              </h2>
              <p class="mt-2">
                {{ $t("invitation_description") }}
              </p>
            </div>

            <div class="mt-6 max-w-lg lg:mt-0 lg:flex lg:flex-col lg:justify-between">
              <div
                class="hidden text-right"
                :class="{ 'lg:block': display === 'form' }"
              >
                <LanguageDropdown />
              </div>

              <div>
                <LoginOrRegisterForm
                  source="collection-invite"
                  :collection-hashid="collectionHashid"
                  :button-label="$t('cobrand_and_share')"
                  button-color="brand"
                  inline
                  @success="onSuccess"
                />
                <Markdown
                  as="p"
                  class="ml-auto mt-4 text-xs text-supplement md:px-0 lg:mt-3 lg:text-right"
                  :text="$t('enter_email_legal')"
                />
              </div>

              <div />
            </div>
          </div>
        </Container>
      </div>
    </div>

    <div class="relative flex grow flex-col">
      <LandingPage
        mode="shared-landing-page"
        :data="data.data.data"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { LoginOrRegisterResponse } from '~/components/forms/LoginOrRegisterForm.vue'

definePageMeta({
  layout: false,
  name: 'collection-invite',
  middleware: [
    function (to) {
      // Retrieve personalisation hash ID from slug and add to route meta
      const { slug } = to.params
      const lastHyphenIndex = slug.lastIndexOf('-')
      const collectionHashid = slug.substring(lastHyphenIndex + 1)
      to.meta.collectionHashid = collectionHashid
    },
    'auto-accept-invite'
  ]
})

const collectionHashid = useRoute().meta.collectionHashid

const { data } = await useAsyncData<
  LaravelResponse<{
    data: LandingPage
  }>
>(`invite-${collectionHashid}`, () => $larafetch(`collections/${collectionHashid}/invite`), {
  transform: (response) => {
    response.data.data.collection.blocks
      .filter(block => block.type === 'itinerary')
      .map(block => (block.item.primary_cta_link = block.item.primary_cta_link || '#'))

    return response
  }
})

if (!data.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
}

const landingPage = toRef(data.value.data, 'data')

const usesGoogleFonts = computed(
  () =>
    landingPage.value.collection.customisations.body_font_type === 'google-font'
    || landingPage.value.collection.customisations.collection_title_font_type === 'google-font'
    || landingPage.value.collection.customisations.content_title_font_type === 'google-font'
)

const { t, locale } = useI18n()

const setDemoContact = () => {
  landingPage.value.contact = {
    alt_id: '',
    type: 'user',
    first_name: t('your'),
    last_name: t('name'),
    email: `${t('you_email_com')}@email.com`,
    phone: t('demo_phone_number')
  }
}

setDemoContact()
watch(locale, () => setDemoContact())

landingPage.value.logo = 'demo'

landingPage.value.advisor = {
  personal_photo: {
    id: null,
    name: 'demo-avatar.png',
    size: null,
    mime_type: 'image/jpeg',
    thumb_url: '/demo-avatar.png',
    full_url: '/demo-avatar.png',
    generated_conversions: []
  }
}

const display = ref<'form' | 'new_user' | 'existing_user'>('form')
const onSuccess = ({ new_user }: LoginOrRegisterResponse) => (display.value = new_user ? 'new_user' : 'existing_user')

// Head
const { fbAppId, siteName } = useRuntimeConfig().public

useHead({
  title: landingPage.value.collection.name_clean,
  titleTemplate: '%s',
  meta: [
    // General
    { name: 'description', content: landingPage.value.seo.description },
    // OpenGraph
    { property: 'fb:app_id', content: fbAppId },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: landingPage.value.seo.canonical_url },
    { property: 'og:title', content: landingPage.value.seo.title },
    { property: 'og:description', content: landingPage.value.seo.description },
    { property: 'og:image', content: landingPage.value.seo.image_url },
    { property: 'og:locale', content: 'en' },
    { property: 'og:site_name', content: siteName },
    // Twitter Card
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: landingPage.value.seo.title },
    { name: 'twitter:description', content: landingPage.value.seo.description },
    { name: 'twitter:image', content: landingPage.value.seo.image_url }
  ]
})

if (usesGoogleFonts.value) {
  useHead({
    link: [
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossorigin: true
      },
      {
        rel: 'stylesheet',
        href: `https://fonts.googleapis.com/css2?family=${landingPage.value.collection.customisations.google_fonts.uri}&display=swap`
      }
    ]
  })
}
</script>
